import { Component, Inject, Input } from '@angular/core';
import {
	UntypedFormControl,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogTitle,
	MatDialogContent,
	MatDialogActions,
	MatDialogClose,
} from '@angular/material/dialog';
import { PhoneNumber } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';
import { EditProfileMode } from 'src/app/ui/profile/profile.component';
import { MatDivider } from '@angular/material/list';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { PhoneTypeFormComponent } from '../phones-form/phone-type-form.component';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { VerificationCodeComponent } from '../verification-code/verification-code.component';
import { PhoneNumberPipe } from '../pipes';

@Component({
	selector: 'app-change-phone-email-dialog',
	templateUrl: './change-phone-email-dialog.component.html',
	styleUrls: [
		'./change-phone-email-dialog.component.scss',
		'../../ui/common/main.scss',
	],
	imports: [
		MatDialogTitle,
		MatDivider,
		CdkScrollable,
		MatDialogContent,
		PhoneTypeFormComponent,
		MatDialogActions,
		MatButton,
		MatDialogClose,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		ReactiveFormsModule,
		VerificationCodeComponent,
		PhoneNumberPipe,
	],
})
export class ChangePhoneEmailDialogComponent {
	emailFormControl = new UntypedFormControl('', [
		Validators.required,
		Validators.email,
	]);

	@Input() userId;

	newMobilePhoneNumber: PhoneNumber | undefined;
	userProfile: UserProfile;
	mode: EditProfileMode;
	editProfileMode = EditProfileMode;
	errorState = false;
	step = 1;

	get isNewMobileNumberNextDisabled(): boolean {
		return this.newMobilePhoneNumber?.rawPhoneNumber !== undefined;
	}

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: { userProfile: UserProfile; mode: EditProfileMode },
		private readonly dialogRef: MatDialogRef<ChangePhoneEmailDialogComponent>,
		private readonly apiService: ApiService
	) {
		this.userProfile = data.userProfile;
		this.mode = data.mode;
		this.userId = data.userProfile.user.id;
	}

	async requestVerificationCode(): Promise<void> {
		switch (this.mode) {
			case EditProfileMode.mobilePhoneNumber:
				if (this.newMobilePhoneNumber?.rawPhoneNumber !== undefined) {
					await this.apiService.requestPhoneVerificationCode(
						this.userId,
						this.newMobilePhoneNumber?.rawPhoneNumber
					);
				}
				break;
			case EditProfileMode.email:
				if (this.emailFormControl.valid) {
					await this.apiService.requestEmailVerificationCode(
						this.emailFormControl.value
					);
				}
				break;
		}
		this.step = 3;
	}

	async verificationCodeEventResult(event: boolean): Promise<void> {
		switch (this.mode) {
			case EditProfileMode.mobilePhoneNumber:
				if (event && this.newMobilePhoneNumber?.rawPhoneNumber !== undefined) {
					await this.apiService.saveMobilePhoneNumber(
						this.userId,
						this.newMobilePhoneNumber
					);
					this.step = 4;
				} else {
					this.step = 5;
				}
				break;

			case EditProfileMode.email:
				if (event && this.emailFormControl.valid) {
					await this.apiService.saveEmail(
						this.userId,
						this.emailFormControl.value
					);
					this.step = 4;
				} else {
					this.step = 5;
				}
				break;
		}
	}

	onCancelClicked(): void {
		this.dialogRef.close();
	}

	onConfirmedClicked(): void {
		this.dialogRef.close();
	}

	onBackClicked(): void {
		this.step = 1;
		this.errorState = false;
	}

	onChangeClicked(): void {
		this.step = 2;
	}

	async onCheckEmail(): Promise<void> {
		const validEmail = await this.apiService.validateEmail(
			this.emailFormControl.value
		);
		if (!validEmail) {
			this.step = 0;
			this.errorState = true;
		} else {
			this.step = 2;
		}
	}

	updateUserPhone(mobilePhone: PhoneNumber): void {
		this.newMobilePhoneNumber = mobilePhone;
	}

	onRetryClicked(): void {
		this.step = 2;
	}
}
